import { path } from './client'
import type { ApiClient, Query } from './client'
import type { Instant, OrgRole, ProjectMember, StaffStatus, StaffUser } from './types'

/**
 * Two kinds of person, and they are not the same kind at all.
 *
 * `staff` are the organization's own users: they have an email, a password and
 * a role, and the organization owns the row. `members` are people with phones
 * who joined a project. An account belongs to nobody, and a member row is only
 * the link between one and a project, which is the entire basis on which this
 * organization is allowed to see anything about them.
 *
 * They live in one module because they are both "people the console lists", and
 * the comment above is here so that nothing in the UI starts treating the
 * second like the first.
 *
 * Both lists are pages on the wire. `GET /v1/org/users` and
 * `GET /v1/org/projects/:id/members` each answer `{ items, nextCursor? }`, and
 * a caller that treated either as a bare array got an object with no `length`
 * and no iterator: the roster lookup below was `for (const user of ...)` over
 * one of them, which throws before anything renders and takes the whole screen
 * with it.
 */

/**
 * An invitation carries an address and a role, and nothing else.
 *
 * There is no name here, because the person names themselves when they accept:
 * `displayName` is a required field on `POST /v1/org/auth/invite/accept`. The
 * form used to collect one, mark it required, and drop it on the way out, which
 * is a field an administrator cannot leave blank and whose value is discarded.
 * The right answer to "what is this person called" is the person.
 */
export interface InviteInput {
  email: string
  role: OrgRole
}

/**
 * A pending invitation, which is what `POST /v1/org/users/invite` answers with.
 *
 * Not a `StaffUser`, and the difference is not pedantry. The invited person has
 * no user row, no name and no status until they accept, so the console's belief
 * that a staff row in `invited` appears in the team list straight afterwards is
 * not true of this server: `GET /v1/org/users` returns accepted staff only, and
 * pending invitations are a list of their own at `GET /v1/org/users/invites`.
 * Typing this as a user meant a team list that showed nothing at all after a
 * successful invitation, which reads exactly like the invitation having failed.
 */
export interface StaffInvite {
  id: string
  email: string
  role: OrgRole
  invitedBy: string
  expiresAt: Instant
  createdAt: Instant
}

export interface StaffUpdateInput {
  role?: OrgRole
  status?: 'active' | 'suspended'
}

export interface MemberListQuery extends Query {
  /** Members of this project. Required: there is no org-wide roster. */
  projectId: string
  /** Default false. Somebody who left is still part of last quarter's numbers. */
  includeLeft?: boolean
  limit?: number
  cursor?: string
}

/**
 * A staff user exactly as `/v1/org/users` sends one.
 *
 * Two field names differ from the console's `StaffUser` and both were silently
 * wrong before this existed. `displayName` arrived where `name` was read, so
 * every screen that prints a member of staff, from the team list to the case
 * timeline to the audit filter to the assignee column, rendered an empty string
 * for a person whose name was in the response the whole time.
 *
 * Mapped here rather than by renaming `StaffUser` across the console, which is
 * the change this deserves and is also a rename through forty test fixtures and
 * eight screens owned by other parts of the app. One adapter in the module that
 * does the fetching is the containable version of the same fix.
 *
 * `lastLoginAt` is read into `lastSeenAt` and the two are not synonyms: a
 * person who signed in on Monday and has had the console open since is last
 * *seen* now and last *logged in* three days ago. The server tracks logins and
 * nothing else, so the weaker fact is what the column shows.
 */
interface StaffUserWire {
  id: string
  email: string
  displayName: string
  role: OrgRole
  status: StaffStatus
  lastLoginAt: Instant | null
  createdAt: Instant
}

function toStaffUser(wire: StaffUserWire): StaffUser {
  return {
    id: wire.id,
    email: wire.email,
    name: wire.displayName,
    role: wire.role,
    status: wire.status,
    lastSeenAt: wire.lastLoginAt,
    createdAt: wire.createdAt,
  }
}

export function membersApi(http: ApiClient) {
  return {
    staff: {
      /**
       * The whole roster, walked to the end of the cursor.
       *
       * Every caller uses this as a lookup from a staff id to a name, so a
       * first page would be an organization where the twenty-sixth person to
       * join is rendered as a raw id on every screen that names an assignee.
       * `collect` stops at its own ceiling rather than looping, and an
       * organization with more staff than that is one where a roster in memory
       * is the wrong shape entirely.
       */
      async list(): Promise<StaffUser[]> {
        const collected = await http.collect<StaffUserWire>('/v1/org/users')
        return collected.items.map(toStaffUser)
      },

      /**
       * Sends an invitation. The person does not exist until they accept it.
       *
       * The pending invitation comes back so the console can put a row on
       * screen without a second request and show the real expiry rather than
       * one computed here. Nothing in it substitutes for the email: only the
       * hash of the token is stored, so there is no link in this response.
       */
      invite(input: InviteInput) {
        return http.request<StaffInvite>('/v1/org/users/invite', {
          method: 'POST',
          body: { email: input.email, role: input.role },
        })
      },

      /**
       * The invitations nobody has accepted yet.
       *
       * A second request rather than a field on the roster, because the server
       * keeps them apart and is right to: an invitation is not a user. It has no
       * name, no password and no id that survives being accepted, and folding it
       * into `/v1/org/users` would mean a list whose rows are two different
       * kinds of thing with half the columns null on one of them.
       *
       * The console has to make that call, and until this existed it was making
       * the wrong one. The team screen rendered `/v1/org/users` alone and that
       * endpoint answers with accepted staff only, so a successful invitation
       * put nothing on screen at all: the dialog closed, the list reloaded
       * unchanged, and the only reading available to the person who pressed the
       * button was that it had failed. They then sent it again.
       *
       * Walked to the end for the same reason the roster is. An invitation
       * missing from this list is an invitation somebody sends twice.
       */
      async invites(): Promise<StaffInvite[]> {
        const collected = await http.collect<StaffInvite>('/v1/org/users/invites')
        return collected.items
      },

      /**
       * Calls an invitation back before it is accepted.
       *
       * Distinct from removing a staff user and not a variation on it. There is
       * no account yet, so there is nothing to suspend, nothing to reassign and
       * no audit trail to preserve against a person: what exists is a live
       * single-use link, and this is what stops it working. The server answers
       * 404 for an id that is not this organization's pending invitation, which
       * covers one already accepted in the moment between the list and the
       * click.
       */
      revokeInvite(id: string) {
        return http.request<void>(path`/v1/org/users/invites/${id}`, { method: 'DELETE' })
      },

      async update(id: string, input: StaffUpdateInput): Promise<StaffUser> {
        const updated = await http.request<StaffUserWire>(path`/v1/org/users/${id}`, {
          method: 'PATCH',
          body: input,
        })
        return toStaffUser(updated)
      },

      /**
       * Removes a staff user from the organization.
       *
       * Their cases and tasks are reassigned by the server rather than deleted;
       * the audit trail keeps their name against what they did. A leaver whose
       * history disappears with them is how a protection log stops being able
       * to answer who opened a record.
       */
      remove(id: string) {
        return http.request<void>(path`/v1/org/users/${id}`, { method: 'DELETE' })
      },
    },

    members: {
      /**
       * One page of a project's roster.
       *
       * Filtering is `includeLeft` and nothing else, which is narrower than
       * this used to claim. A `q` and a `source` were sent and the server
       * dropped both, unrecognised query parameters being ignored rather than
       * refused, so the console returned the unfiltered list and presented it
       * as the filtered one. `q` cannot come back: there is nothing here to
       * search, because the roster carries account ids and no names at all.
       */
      list({ projectId, ...query }: MemberListQuery) {
        return http.list<ProjectMember>(path`/v1/org/projects/${projectId}/members`, query)
      },

      /**
       * Ends a membership. Not a delete.
       *
       * `left_at` is a timestamp because a deleted row silently rewrites every
       * number that was already reported: a person who leaves in April must not
       * change what March's dashboard said. The server answers with the
       * membership it just ended, carrying that timestamp, so the screen can
       * say when rather than asking again.
       *
       * Repeating the call is not an error. It reports the date the first one
       * wrote, so two clicks on a slow connection cannot move a figure a report
       * has already been drawn from.
       */
      end(projectId: string, memberId: string) {
        return http.request<ProjectMember>(
          path`/v1/org/projects/${projectId}/members/${memberId}`,
          { method: 'DELETE' },
        )
      },
    },
  }
}

export type MembersApi = ReturnType<typeof membersApi>
